// src/frontend/pages/ContentViewerSolidity.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import GenericBookChapter from '../data/books/solidity/solidityBook';
import SPDX_LICENSES from "../data/books/solidity/data/spdxLicenses";
import PRAGMA_SOLIDITY from "../data/books/solidity/data/PRAGMA_SOLIDITY";
import PRAGMA_VERSIONS from "../data/books/solidity/data/pragmaVersions";
import SOLIDITY_BASE_SYNTAX from "../data/books/solidity/data/solidityBaseSyntax";
import SOLIDITY_TYPES from "../data/books/solidity/data/solidityTypes";
import REMIX_WARNINGS from "../data/books/solidity/data/remixWarnings";

const TITLE_BLUE = "#175cd3";
const SIDEBAR_BG = "#0f2147";       // look “dashboard”
const SIDEBAR_BG_SOFT = "#132a55";
const SIDEBAR_BORDER = "#0b1a3a";
const ITEM_TEXT = "#ffffffff";
const ITEM_TEXT_ACTIVE = "#ffffff";

const BASE_PATH = "/course/solidity-book";

/** Registro de componentes externos por nombre */
const ComponentRegistry = {
  SPDXLicenses: () => <GenericBookChapter data={SPDX_LICENSES} />,
  PRAGMASolidity: () => <GenericBookChapter data={PRAGMA_SOLIDITY} />,
  PRAGMAVersion: () => <GenericBookChapter data={PRAGMA_VERSIONS} />,
  SintasisBase: () => <GenericBookChapter data={SOLIDITY_BASE_SYNTAX} />,
  remixWarnings: () => <GenericBookChapter data={REMIX_WARNINGS} />,
  SolidityTypes: () => <GenericBookChapter data={SOLIDITY_TYPES} />,
};

/** Bloques demo opcionales */
function IntroSolidity() {
  return (
    <article className="vv-article">
      <h2>Introducción a Solidity</h2>
      <p>
        <strong>Solidity</strong> es un lenguaje orientado a contratos para la EVM y cadenas compatibles.
        Permite definir estados, funciones y eventos que se ejecutan on-chain.
      </p>
      <h3>Conceptos esenciales</h3>
      <ul>
        <li><strong>pragma solidity</strong>: versión de compilador.</li>
        <li><strong>Tipos</strong>: <code>uint</code>, <code>address</code>, <code>bool</code>, <code>string</code>.</li>
        <li><strong>Visibilidad</strong>: <code>public</code>, <code>external</code>, <code>internal</code>, <code>private</code>.</li>
        <li><strong>Modificadores</strong>: <code>view</code>, <code>pure</code>, <code>payable</code>.</li>
        <li><strong>Eventos</strong> + <code>emit</code>.</li>
      </ul>
    </article>
  );
}

function SolidityReactDemo() {
  const [mensaje, setMensaje] = useState("GM, Blockchain!");
  const sampleABI = useMemo(
    () => ([
      { "inputs":[{ "internalType":"string","name":"_mensaje","type":"string"}],"stateMutability":"nonpayable","type":"constructor" },
      { "inputs":[],"name":"getMensaje","outputs":[{ "internalType":"string","name":"","type":"string"}],"stateMutability":"view","type":"function" },
      { "inputs":[{ "internalType":"string","name":"_nuevo","type":"string"}],"name":"setMensaje","outputs":[],"stateMutability":"nonpayable","type":"function" }
    ]),
    []
  );

  return (
    <section className="vv-demo">
      <h2>Componente React — Mini demo</h2>
      <div className="vv-card">
        <div className="vv-row">
          <label className="vv-label">Nuevo mensaje</label>
          <input className="vv-input" value={mensaje} onChange={e => setMensaje(e.target.value)} />
        </div>
        <div className="vv-row">
          <button className="vv-btn" onClick={() => alert(`setMensaje('${mensaje}') vía web3`)}>Simular setMensaje</button>
          <button className="vv-btn outline" onClick={() => alert("getMensaje() vía web3")}>Simular getMensaje</button>
        </div>
        <pre className="vv-code small"><code>{JSON.stringify(sampleABI, null, 2)}</code></pre>
      </div>
    </section>
  );
}

/** DATA del índice */
const contentsData = [
  { id: 1, title: "Fundamentos Iniciales de Solidity", type: "external", component: "PRAGMASolidity" },
  { id: 2, title: "Licencias SPDX en Smart Contracts", type: "external", component: "SPDXLicenses" },
  { id: 3, title: "Pragma Solidity — Versiones, Rangos y Buenas Prácticas", type: "external", component: "PRAGMAVersion" },
  { id: 4, title: "Remix IDE — Cómo evitar warnings y errores comunes", type: "external", component: "remixWarnings" },
  { id: 5, title: "Solidity — Sintaxis Base", type: "external", component: "SintasisBase" },
  { id: 6, title: "Tipos de Datos en Solidity", type: "external", component: "SolidityTypes" },
];

export default function ContentViewerSolidity() {
  const { contentId } = useParams();
  const navigate = useNavigate();

  const [selected, setSelected] = useState(
    () => contentsData.find((c) => c.id === Number(contentId)) || contentsData[0]
  );

  // ← NUEVO: colapso de sidebar
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const found = contentsData.find((c) => c.id === Number(contentId));
    if (found) setSelected(found);
  }, [contentId]);

  const handleSelect = (item) => navigate(`${BASE_PATH}/${item.id}`);

  return (
    <div className="vv-serif-root vv-wrap" data-collapsed={collapsed ? "true" : "false"}>
      <style>{`
  :root {
    /* si tienes header global, pon su alto aquí (p.ej. 64px) */
    --header-offset: 11vh;
  }

  /* ===== Layout ===== */
  .vv-wrap{
    --sb-w-expanded: 340px;
    --sb-w-collapsed: 84px;
    --sb-w: var(--sb-w-expanded);
    display:block;
    min-height:100dvh;
    background:#fff;
  }
  .vv-wrap[data-collapsed="true"]{ --sb-w: var(--sb-w-collapsed); }

  /* ===== Sidebar FIJA y FULL HEIGHT =====
     Usa inset para asegurar top/bottom pegados al viewport
  */
  .vv-sidebar{
    position:fixed;
    inset: var(--header-offset) auto 0 0; /* top, right, bottom, left */
    width:var(--sb-w);
    background:#0f2147;
    border-right:1px solid #0b1a3a;
    display:grid;
    grid-template-rows:auto 1fr auto;
    z-index:30;
    overflow:hidden; /* el scroll va adentro */
  }
  .vv-side-scroll{ overflow:auto; padding:10px 10px 16px 10px; }

  .vv-title{
    display:flex; align-items:center; gap:10px;
    background:#175cd3; color:#fff; font-weight:800; font-size:16px;
    padding:16px 14px; margin:0; border-bottom:1px solid rgba(255,255,255,.15);
    white-space:nowrap;
  }
  .vv-collapse{
    position:absolute; top:12px; right:12px;
    width:34px; height:34px; border-radius:999px;
    border:1px solid rgba(255,255,255,.25);
    background:#132a55; color:#fff; font-weight:800; cursor:pointer;
  }

  .vv-items{ list-style:none; margin:8px 0 0; padding:0; }
  .vv-itemBtn{
    all:unset;
    display:grid; grid-template-columns:28px 1fr auto; align-items:center; gap:12px;
    width:100%; padding:10px 12px; margin:2px 6px; cursor:pointer; border-radius:10px;
    color:#c9ddff;
  }
  .vv-itemBtn:hover{ background:#132a55; }
  .vv-itemActive{ background:#132a55; outline:1px solid rgba(255,255,255,.08); color:#fff; }

  .vv-dot{ width:22px; height:22px; display:grid; place-items:center; border-radius:6px;
           border:1px solid rgba(255,255,255,.15); font-size:11px; color:#9fc0ff; }

  /* títulos largos: dos líneas, se ajustan bonito */
  .vv-itemLabel{
    font-size:14px; line-height:1.25;
    display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden;
  }

  /* Estado colapsado: solo iconos centrados */
  .vv-wrap[data-collapsed="true"] .vv-itemLabel{ display:none; }
  .vv-wrap[data-collapsed="true"] .vv-itemBtn{ grid-template-columns:1fr; justify-items:center; }
  .vv-wrap[data-collapsed="true"] .vv-title{ font-size:0; padding-right:52px; }
  .vv-wrap[data-collapsed="true"] .vv-title::after{
    content:"S"; font-size:16px; font-weight:900; letter-spacing:.1em;
  }

  .vv-side-footer{ border-top:1px solid #0b1a3a; padding:10px 12px; color:#9fb7ff; font-size:12px; background:#0d1f48; }

  /* ===== Viewer: se corre a la derecha EXACTAMENTE el ancho de la barra ===== */
  .vv-viewer{
    margin-left:var(--sb-w);
    padding:18px clamp(12px, 2vw, 28px);
    min-height:calc(100dvh - var(--header-offset));
    display:flex; justify-content:center; align-items:flex-start;
  }
  .vv-frameWrap{ width:98%; max-width:1100px; }

  /* Contenido */
  .vv-article h2{ font-size:28px; margin:0 0 10px; color:#0f172a; }
  .vv-article h3{ font-size:18px; margin:18px 0 8px; color:#0f172a; }
  .vv-article p,.vv-article li{ font-size:16px; color:#334155; line-height:1.6; }
  .vv-card{ border:1px solid #e6ebf1; border-radius:12px; padding:14px; background:#fff; box-shadow:0 8px 24px rgba(2,20,46,.04); margin-bottom:16px; }
  .vv-row{ display:grid; gap:10px; margin:10px 0; }
  .vv-label{ font-weight:700; color:#0f172a; }
  .vv-input{ border:1px solid #e6ebf1; border-radius:10px; padding:10px 12px; outline:none; }
  .vv-btn{ border:1px solid #e6ebf1; border-radius:10px; padding:10px 14px; font-weight:700; cursor:pointer; background:#fff; }
  .vv-code{ background:#0b1220; color:#e2e8f0; padding:14px; border-radius:10px; overflow:auto; border:1px solid rgba(120,170,255,.25); }
  .vv-code.small{ font-size:12px; }

  /* IMPORTANTE: sin override móvil que contraiga la altura.
     La barra siempre ocupa todo el alto también en mobile. */
`}</style>


      {/* SIDEBAR fija */}
      <aside className="vv-sidebar" aria-label="Navegación de Contenidos">
        <h1 className="vv-title">
          <span> Solidity</span>
          <button
            className="vv-collapse"
            title={collapsed ? "Expandir" : "Colapsar"}
            onClick={() => setCollapsed((v) => !v)}
            aria-label="Alternar sidebar"
          >
            {collapsed ? "›" : "‹"}
          </button>
        </h1>

        <div className="vv-side-scroll">
          <ul className="vv-items">
            {contentsData.map((item) => {
              const active = selected.id === item.id;
              return (
                <li key={item.id}>
                  <button
                    className={`vv-itemBtn ${active ? "vv-itemActive" : ""}`}
                    onClick={() => handleSelect(item)}
                    aria-current={active ? "page" : undefined}
                    title={item.title}   /* tooltip nativo en estado colapsado */
                  >
                    <span className="vv-dot">▣</span>
                    <span className="vv-itemLabel">{item.title}</span>
                    {active && <span style={{color:"#8ec5ff", fontWeight:800}}>•</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="vv-side-footer">
          <div>v1.0 • Solidity Book</div>
        </div>
      </aside>

      {/* VIEWER */}
      <main className="vv-viewer">
        <div className="vv-frameWrap">
          {selected?.type === "external" && selected?.component && ComponentRegistry[selected.component] ? (
            React.createElement(ComponentRegistry[selected.component])
          ) : selected?.type === "intro" ? (
            <IntroSolidity />
          ) : selected?.type === "component" ? (
            <SolidityReactDemo />
          ) : selected?.type === "book" && selected?.embed ? (
            <iframe
              key={selected.id}
              src={selected.embed}
              className="vv-iframe"
              allow="autoplay"
              allowFullScreen
              title={selected.title}
              style={{ width: "100%", height: 560, border: 0 }}
            />
          ) : (
            <div style={{ padding: 24, textAlign: "center", border: "1px dashed #cbd5e1", borderRadius: 8 }}>
              <p style={{ margin: 0, color: "#475569" }}>
                Configura este ítem en <code>contentsData</code>.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
